/**
 * Contract-position status → colour band, extracted so BOTH the tree's
 * `ContractPositionStatusChip` AND the project map markers (FE-1, the
 * create/edit-unification map-colours change) consume ONE palette — the
 * map never re-invents the hexes.
 *
 * Band mapping (per `03-page-contract-position.md` §Table column 5), and the
 * D81 slot each band now means:
 *   white  ← CREATED, SCHEDULED   → neutral   (inert · not started)
 *   yellow ← EXECUTION            → attention (in progress, needs a human)
 *   green  ← COMPLETED            → success   (done)
 *   red    ← WITHDRAWN, REJECTED  → critical  (terminal/abandoned)
 *
 * The chip renders the band as a TONAL fill (hue at 18% over the surface); the
 * MAP needs the RAW HUE, opaque — markers sit over a basemap, so a tonal chip
 * fill would muddy against the tiles and, worse, would be a fill computed over
 * a surface that isn't behind it. `markerColorForState` therefore reads
 * `color.<mode>.status[slot]` directly rather than going through
 * `statusChipStyle`. A turbine with no position falls back to the neutral
 * default (`MARKER_COLOR_DEFAULT`).
 */
import { color } from "@aeriosiq/ui";
import type { ContractPositionState } from "./types.js";

export type ContractPositionColorBand = "white" | "yellow" | "green" | "red";

/** The canonical state → colour-band map. The single source of truth shared by
 *  the status chip and the map markers. */
export const CONTRACT_POSITION_STATE_COLOR: Record<
  ContractPositionState,
  ContractPositionColorBand
> = {
  CREATED: "white",
  SCHEDULED: "white",
  EXECUTION: "yellow",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

/**
 * The marker palette is pinned to the LIGHT mode's status hues.
 *
 * `markerColorForState` is theme-less by signature (it is also called from a
 * raw-DOM marker factory and from a legend built outside any `sx` callback),
 * so a mode has to be chosen rather than resolved. Light is the correct one:
 * the dots sit on the basemap tiles behind a white ring, not on the app's
 * surface, and the light slots are the darker, more saturated end of each hue —
 * the pair that reads against map tiles. The dark slots are lifted for a dark
 * app surface and would wash out there.
 */
const MARKER_MODE = "light" as const;

/** Neutral marker colour for a turbine with NO contract position (decision: a
 *  turbine that isn't linked into the project hierarchy yet). The `neutral`
 *  slot's raw hue — it reads as "no status" rather than as any band, which is
 *  exactly what the slot means. */
/**
 * "This turbine has no contract position" — the ABSENCE of a state, not a state.
 *
 * It must not equal the `white` band, which means CREATED/SCHEDULED: those are
 * real states that happen to be inert, and the legend lists them separately. A
 * first pass pointed both at `status.neutral` and collapsed the two legend
 * swatches into one, which is a loss of information rather than a simplification.
 *
 * `borders.control` is the system's non-text neutral — deliberately below the
 * text floor and above the 3:1 non-text bar. A marker for missing data reading
 * quieter than a marker for a real state is the right way round.
 */
export const MARKER_COLOR_DEFAULT = color[MARKER_MODE].borders.control;

/** Opaque solid marker colours per band — the RAW slot hue, not the chip's
 *  tonal fill (see the header). */
const MARKER_COLOR_BY_BAND: Record<ContractPositionColorBand, string> = {
  // CREATED / SCHEDULED — the neutral slot (not started).
  white: color[MARKER_MODE].status.neutral,
  // EXECUTION — the attention slot (in progress).
  yellow: color[MARKER_MODE].status.attention,
  // COMPLETED — the success slot (done).
  green: color[MARKER_MODE].status.success,
  // WITHDRAWN / REJECTED — the critical slot (terminal).
  red: color[MARKER_MODE].status.critical,
};

/** The opaque solid marker colour for a contract-position state. */
export function markerColorForState(state: ContractPositionState): string {
  return MARKER_COLOR_BY_BAND[CONTRACT_POSITION_STATE_COLOR[state]];
}
